import { useSearch } from "@sinch/hooks/utilities/useSearch";
import { isEmpty, length, range } from "ramda";
import { isArray, isFunction, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useEffect, useMemo, useState } from "react";
import {
  flatOptions,
  getDepth,
  getLeafList,
  searchPath,
} from "@sinch/components/form/MultilevelSelect/hooks/optionTreeTools";
import { useAsyncLoadChildren } from "@sinch/components/form/MultilevelSelect/hooks/useAsyncLoadChildren";
import { ListColumnProps, NestedOptions } from "@sinch/components/form/MultilevelSelect/types";

/**
 * Custom hook to manage state and logic for a multilevel select component.
 * Supports nested options, search functionality, and dynamic level rendering.
 */
export const useMultilevelSelect = <TValue,>({
  options,
  onChange,
  value,
}: {
  onChange?: (e: React.MouseEvent<HTMLLIElement>, val: TValue) => void;
  options: NestedOptions<TValue>[];
  value: TValue | null;
}) => {
  const pathByValue = useMemo(() => (isNotNil(value) ? (searchPath(options, value) ?? []) : []), [value, options]);
  // State to track the selected path in the nested options
  const [selectedPath, setSelectedPath] = useState<number[]>(pathByValue);
  // State to track the search query
  const [search, setSearch] = useState<string>("");

  // Memoized flattened options for search functionality
  const structuredOptions = useMemo(
    () => (isNotNilOrEmpty(search) ? flatOptions(options) : options),
    [options, search]
  );

  const { memoizeLoading, loading, error } = useAsyncLoadChildren();

  // Search function to filter options based on the search query
  const searchFn = useSearch<NestedOptions<TValue>>(structuredOptions, [["label"]]);
  // Search results or original options if no search query is present
  const searchResult = isNotNilOrEmpty(search) ? searchFn(search) : options;
  /**
   * Retrieves the list of options for a specific level based on the selected path.
   */
  const getLevelList = (level: number): NestedOptions<TValue>[] | Promise<void> => {
    if ((length(selectedPath) <= 1 && level === 0) || level === 0) {
      return searchResult;
    }
    if (level > selectedPath.length) {
      return [];
    }
    return (length(selectedPath) <= 1 && level === 0) || level === 0
      ? searchResult
      : (selectedPath.slice(0, level).reduce<NestedOptions<TValue>[] | Promise<void> | null>((acc, index) => {
          const previousVal = acc ?? searchResult;
          if (isArray(previousVal)) {
            const children = previousVal[index]?.children ?? [];
            return isArray(children) ? children : memoizeLoading(index, children);
          }
          return previousVal;
        }, null) ?? []);
  };

  /**
   * Handles selection of an option at a specific level.
   */
  const handleSelect = (level: number, index: number) => {
    setSelectedPath((prev) => [...prev.slice(0, level), index]);
  };

  /**
   * Handles changes to the search query.
   */
  const handleSearch = (val: string) => {
    if (length(val) >= 3) {
      setSelectedPath([]);
    }
    setSearch(val);
    if (isEmpty(val)) {
      // Reset to the first option if the search query is cleared
      handleSelect(0, 0);
    } else {
      // Clear the selected path when searching
      setSelectedPath([]);
    }
  };

  useEffect(() => {
    setSelectedPath(pathByValue);
  }, [value]);

  useEffect(() => {
    if (length(search) >= 3) {
      getLeafList(options)?.forEach((item, index) => {
        if (isFunction(item.children)) {
          memoizeLoading(index, item.children);
        }
      });
    }
  }, [search, options]);

  // Generate columns for each level with their respective options and event handlers
  const listColumn: ListColumnProps<TValue>[] = range(0, getDepth(searchResult)).map((level) => ({
    level,
    list: getLevelList(level) ?? [],
    onOpenList: (index: number) => handleSelect(level, index),
    onChange: onChange,
    selected: selectedPath[level] ?? null,
  }));

  return {
    listColumn,
    search,
    onSearch: handleSearch,
    loading,
    selectedPath,
    error,
  };
};
